// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { lazy, Suspense, useMemo } from 'react';
import { sanitizeVegaSpec } from './sanitizeVegaSpec';

/**
 * Lazy-loaded Vega-Lite renderer. vega-embed + vega-lite + vega weighs
 * ~400 KB gzipped — too heavy to ship on the main advisor-ui bundle
 * just for the chatbot. React.lazy + Suspense pushes it onto a separate
 * chunk that's only fetched the first time a chart appears in a reply.
 *
 * react-vega 8 exports `VegaEmbed` which accepts any vega-embed-
 * compatible spec (Vega or Vega-Lite). We hand it the Vega-Lite spec
 * the LLM emitted; vega-embed picks the right grammar by the spec's
 * `$schema` URL.
 */
const VegaEmbedComponent = lazy(() =>
    import('react-vega').then((m) => ({ default: m.VegaEmbed })),
);

/**
 * Renders a Vega-Lite spec the LLM emitted as a fenced markdown block.
 * Spec arrives as raw JSON text; we parse + sanitise (strip every `url`
 * / `href` field — defence against prompt-injected data exfiltration)
 * + hand to react-vega.
 *
 * Failure modes:
 *   - JSON parse error → render as a styled code block so the advisor
 *     still sees what the LLM tried to emit.
 *   - Sanitised spec rejected by vega-lite → react-vega surfaces a
 *     console error; we render a small inline notice.
 */
export interface VegaChartProps {
  /** Raw JSON spec text from the fenced ```vega-lite block. */
  specJson: string;
}

export function VegaChart({ specJson }: VegaChartProps) {
  const result = useMemo(() => {
    try {
      const parsed = JSON.parse(specJson);
      const sanitised = sanitizeVegaSpec(parsed);
      return { ok: true as const, spec: sanitised };
    } catch (err) {
      return { ok: false as const, error: (err as Error).message };
    }
  }, [specJson]);

  if (!result.ok) {
    return (
      <pre
        data-testid='vega-chart-parse-error'
        style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: 8,
                 borderRadius: 6, fontSize: 12, overflow: 'auto', margin: '4px 0' }}
      >
        {`Chart spec did not parse (${result.error})\n\n${specJson}`}
      </pre>
    );
  }

  return (
    <div
      className='fund-chatbot-vega-chart'
      data-testid='fund-chatbot-vega-chart'
      style={{ margin: '8px 0', maxWidth: '100%', overflow: 'auto' }}
    >
      <Suspense fallback={
        <div style={{ fontSize: 12, color: '#64748b', padding: 8 }}>Rendering chart…</div>
      }>
        <VegaEmbedComponent
          spec={result.spec as never}
          options={{ actions: false, renderer: 'svg' }}
        />
      </Suspense>
    </div>
  );
}
